import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { isCancelable, isReschedulable } from '@domain/entities/VacationRequest';
import { signatureTrail } from '@domain/policies/ApprovalPolicy';
import type { RequestApproval, VacationRequest } from '@domain/entities/VacationRequest';
import { reasonsForScope } from '@domain/entities/RuntimeConfig';
import { OCCUPYING_STATUSES, type RequestStatus } from '@domain/vocabulary';
import type { CalendarDate } from '@domain/value-objects/CalendarDate';
import type { DateRange } from '@domain/value-objects/DateRange';
import { getServices } from '@infrastructure/services';
import {
  Avatar,
  Banner,
  Button,
  Card,
  Chip,
  EmptyState,
  Field,
  Modal,
  Spinner,
  cssVars,
  cx,
  Pagination,
} from '../../design-system';
import { usePagination } from '../../hooks/usePagination';
import {
  MONTHS_SHORT,
  formatDateRange,
  formatDayWithYear,
  formatNaturalDays,
  statusAccentColor,
  statusChipColors,
} from '../../format';
import { lifecycleSteps } from '../../lifecycle';
import { useAccess } from '../../hooks/useAccess';
import { useVacationEligibility } from '../../hooks/useVacationEligibility';
import { useSession } from '../../providers/SessionProvider';
import { useToast } from '../../providers/ToastProvider';
import shared from '../shared.module.css';
import { RequestWizard } from './RequestWizard';
import type { PersonId } from '@shared/brand';
import { seedColorForSubArea } from '@domain/entities/Organization';
import { foldForSearch } from './candidateSearch';
import { awayDuring } from './replacementAvailability';
import { serverAnswer } from '../../serverAnswer';
import { groupRescheduleChains } from './rescheduleChains';
import { ReplacementInbox } from './ReplacementInbox';
import styles from './RequestsScreen.module.css';
import { loadFailed } from '../../loadError';
import { ReasonPicker } from '../ReasonPicker';
import { PageTitle } from '../../shell/PageTitle';

/**
 * Mis solicitudes — screen 4, transcribed from the prototype.
 *
 * Each request is a four-cell card: a status-coloured date box, the body, the
 * signature trail, and the status column with its actions. Cancelling demands a
 * reason from the runtime catalogue — an arbitrary string tells a later reader
 * nothing and cannot be aggregated in the reports.
 */

/**
 * Four filters, not one per status.
 *
 * «Cerradas» gathers everything already settled — taken, rejected, cancelled.
 * Splitting those into three tabs would give the two a person actually acts on
 * equal billing with three they can only read.
 */
type RequestFilter = 'todas' | 'revision' | 'aprobadas' | 'tomadas' | 'cerradas';

const FILTERS: readonly { id: RequestFilter; label: string }[] = [
  /*
   * «Vigentes» y ya no «Todas», porque dejó de mostrarlas todas.
   *
   * Al sacar de aquí lo que ya ocurrió, el rótulo pasó a prometer más de lo que
   * enseña — y una pestaña que dice «todas» y esconde la mitad manda a buscar un
   * folio que sí está, en la pestaña de al lado. Lo que queda aquí es lo que
   * todavía puede cambiar; el resto vive en «Tomadas» y «Cerradas».
   */
  { id: 'todas', label: 'Vigentes' },
  { id: 'revision', label: 'En revisión' },
  { id: 'aprobadas', label: 'Aprobadas' },
  { id: 'tomadas', label: 'Tomadas' },
  { id: 'cerradas', label: 'Cerradas' },
];

const OPEN_STATUSES: readonly RequestStatus[] = ['Por revisar', 'En revisión', 'Visto bueno'];
const CLOSED_STATUSES: readonly RequestStatus[] = ['Tomada', 'Rechazada', 'Cancelada'];

/**
 * Una solicitud que ya no puede cambiar y cuya fecha además pasó.
 *
 * **Por fecha y no sólo por estado**, y la diferencia importa: el estado `Tomada`
 * lo pone una tarea nocturna, así que entre que las vacaciones terminan y el
 * trabajo corre hay unas horas en las que la solicitud sigue diciendo «Aprobada».
 * Quien mire su pantalla a las nueve de la mañana del día siguiente vería sus
 * días de ayer todavía anunciados como próximos.
 *
 * **Y por estado además de por fecha**, que es lo que faltaba: una cancelada del
 * 12 al 19 seguía apareciendo el 24 porque sólo se archivaban las tomadas. Una
 * solicitud cancelada o rechazada cuya fecha ya pasó no espera nada de nadie.
 *
 * Lo que **no** se archiva es lo que sigue abierto, aunque la fecha haya pasado:
 * una solicitud sin firmar para la semana pasada no es historia, es un problema —
 * esconderla sería esconder que alguien se quedó sin respuesta.
 */
const SETTLED: readonly RequestStatus[] = ['Tomada', 'Aprobada', 'Cancelada', 'Rechazada'];

const yaOcurrio = (
  request: { status: RequestStatus; range: DateRange },
  today: CalendarDate,
): boolean => SETTLED.includes(request.status) && request.range.end.isBefore(today);

/* Exportado para la prueba: es la regla que decide qué ve alguien al entrar, y
   sus casos de borde son fechas, no interacción. */
export const matchesFilter = (
  request: { status: RequestStatus; range: DateRange },
  filter: RequestFilter,
  today: CalendarDate,
): boolean => {
  const pasada = yaOcurrio(request, today);

  switch (filter) {
    /*
     * «Todas» deja fuera lo que ya ocurrió, que es lo que se pidió: una solicitud
     * para hoy desaparece de aquí mañana. No se pierde — vive en «Tomadas», que
     * existe justo para eso. Sin esa pestaña esto sería esconder, y esconder no
     * es lo mismo que ordenar.
     */
    case 'todas':
      return !pasada;
    /* Ver `yaOcurrio`: lo que sigue abierto se queda aquí aunque la fecha pasara. */
    case 'revision':
      return OPEN_STATUSES.includes(request.status);
    case 'aprobadas':
      /* Las que están por venir. Las de la semana pasada ya no se «esperan». */
      return request.status === 'Aprobada' && !pasada;
    case 'tomadas':
      /* Sólo las que de verdad se tomaron: una cancelada nunca lo fue, y va a
         «Cerradas». Archivar no es lo mismo que haber ocurrido. */
      return pasada && (request.status === 'Tomada' || request.status === 'Aprobada');
    case 'cerradas':
      return CLOSED_STATUSES.includes(request.status);
  }
};

export const RequestsScreen = (): ReactNode => {
  const session = useSession();
  const { holds } = useAccess();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [filter, setFilter] = useState<RequestFilter>('todas');
  const [cancelling, setCancelling] = useState<VacationRequest | null>(null);
  const [repicking, setRepicking] = useState<VacationRequest | null>(null);
  /**
   * Null when closed; the request being replaced when reopening to reschedule.
   *
   * The whole request, not its folio. The wizard needs its length to suggest
   * windows of the same size, and asking the database for it by folio is a query
   * the security rules refuse — a `folio` filter proves neither the area nor the
   * owner. This screen already has the object; handing it over costs nothing.
   */
  const eligibility = useVacationEligibility();
  const [wizard, setWizard] = useState<{ rescheduleFrom: VacationRequest | null } | null>(() =>
    searchParams.get('new') === '1' ? { rescheduleFrom: null } : null,
  );

  useEffect(() => {
    if (searchParams.get('new') !== '1') return;
    setSearchParams((current) => {
      current.delete('new');
      return current;
    }, { replace: true });
  }, [searchParams, setSearchParams]);

  const requests = useQuery({
    queryKey: ['requests', 'mine', session.personId],
    queryFn: async () => {
      const result = await getServices().requests.list(
        { personId: session.personId },
        { limit: 50 },
      );
      if (!result.ok) throw loadFailed('No pudimos cargar tus solicitudes.', result.error);
      return result.value.items;
    },
  });

  const all = useMemo(() => requests.data ?? [], [requests.data]);

  /*
   * One card per story, not per document.
   *
   * A reschedule leaves the request it replaced behind — it has to, a decided
   * request cannot be rewritten and a rejection is evidence — so a person moved
   * twice ended up with three cards for one holiday. The chain collapses to the
   * request that is still live and carries the rest as history.
   *
   * The filter reads the head's status, which is the status of the story: a
   * chain whose latest attempt is «Por revisar» belongs under «En revisión»,
   * however many refusals precede it.
   */
  const chains = useMemo(() => groupRescheduleChains(all), [all]);

  const today = getServices().clock.today();
  const visible = chains.filter((chain) => matchesFilter(chain.head, filter, today));
  /* De diez en diez. Quien lleva años en la empresa tiene decenas de solicitudes,
     y cada una es una tarjeta con su traza de firmas. */
  const pager = usePagination(visible, filter);

  return (
    <div className={shared.screen}>
      <header className={styles.header}>
        <PageTitle className={styles.title}>Solicitudes de vacaciones</PageTitle>
        {/* Gated on the permission, not just on the role: an administrator
            reviews other people's days and files none of their own, so offering
            the action would open a wizard that refuses on the first step. */}
        {holds('solicitar') && (
          <button
            type="button"
            className={styles.primaryAction}
            onClick={() => {
              setWizard({ rescheduleFrom: null });
            }}
          >
            <span className={styles.plus} aria-hidden="true">
              +
            </span>{' '}
            Solicitar vacaciones
          </button>
        )}
      </header>

      {/* Lo que otra persona espera de ti va antes que el estado de lo tuyo, y
          sólo se dibuja cuando hay algo que responder. */}
      <ReplacementInbox />

      {/*
       * Said once, in full, where there is room to say it.
       *
       * The shell's button carries the same date in a tooltip, which is fine for
       * a hover and useless on a phone — and this is the screen somebody comes to
       * when they want to know why the calendar starts where it does.
       */}
      {holds('solicitar') && !eligibility.eligible && eligibility.vestsOn !== null && (
        <Banner tone="info">
          Puedes agendar a partir del {formatDayWithYear(eligibility.vestsOn)}, cuando cumplas tu
          primer año — faltan {formatNaturalDays(eligibility.daysRemaining)}. El año se cuenta de
          tu fecha de ingreso a la misma fecha, y el calendario del asistente empieza ahí.
        </Banner>
      )}

      <div className={cx(styles.segmented, 'scroll-x')} role="tablist" aria-label="Filtrar por estado">
        {FILTERS.map((option) => (
          <button
            key={option.id}
            type="button"
            role="tab"
            aria-selected={filter === option.id}
            className={cx(styles.segment, filter === option.id && styles.segmentActive)}
            onClick={() => {
              setFilter(option.id);
            }}
          >
            {option.label}
          </button>
        ))}
      </div>

      {requests.isLoading ? (
        <Card>
          <Spinner label="Cargando" />
        </Card>
      ) : requests.error !== null ? (
        <Banner tone="danger">No pudimos cargar tus solicitudes.</Banner>
      ) : visible.length === 0 ? (
        <Card>
          <EmptyState
            glyph="↗"
            title={filter === 'todas' ? 'Todavía no has pedido días' : 'Nada con ese estado'}
            note={
              filter === 'todas'
                ? 'Cuando envíes una solicitud aparecerá aquí con su folio y su traza de firmas.'
                : 'Prueba con otro filtro para ver el resto de tus solicitudes.'
            }
          />
        </Card>
      ) : (
        <ul className={styles.list}>
          {pager.items.map((chain) => (
            <RequestCard
              key={chain.head.id}
              request={chain.head}
              history={chain.history}
              onCancel={() => {
                setCancelling(chain.head);
              }}
              onReschedule={() => {
                setWizard({ rescheduleFrom: chain.head });
              }}
              onChangeReplacement={() => {
                setRepicking(chain.head);
              }}
            />
          ))}
        </ul>
      )}

      <Pagination pager={pager} noun="solicitudes" detached />

      {wizard !== null && (
        <RequestWizard
          rescheduleFrom={wizard.rescheduleFrom}
          onClose={() => {
            setWizard(null);
          }}
          onSubmitted={async () => {
            setWizard(null);
            await requests.refetch();
            // The sidebar pill and the home inbox count the same documents, so
            // leaving them cached shows a stale number next to a fresh list.
            await queryClient.invalidateQueries({ queryKey: ['shell'] });
            await queryClient.invalidateQueries({ queryKey: ['home'] });
          }}
        />
      )}

      {repicking !== null && (
        <PickReplacementDialog
          request={repicking}
          onClose={() => {
            setRepicking(null);
          }}
          onDone={async () => {
            setRepicking(null);
            await queryClient.invalidateQueries({ queryKey: ['requests'] });
            await queryClient.invalidateQueries({ queryKey: ['approvals'] });
          }}
        />
      )}

      {cancelling !== null && (
        <CancelDialog
          request={cancelling}
          onClose={() => {
            setCancelling(null);
          }}
          onDone={async () => {
            setCancelling(null);
            await requests.refetch();
          }}
        />
      )}
    </div>
  );
};

const RequestCard = ({
  request,
  history,
  onCancel,
  onReschedule,
  onChangeReplacement,
}: {
  request: VacationRequest;
  /** The attempts this one replaced, most recent first. */
  history: readonly VacationRequest[];
  onCancel: () => void;
  onReschedule: () => void;
  onChangeReplacement: () => void;
}): ReactNode => {
  const colors = statusChipColors(request.status);
  const [showHistory, setShowHistory] = useState(false);

  const approvals = useQuery({
    queryKey: ['requests', 'approvals', request.id],
    queryFn: async () => {
      const result = await getServices().requests.approvalsFor(request.id);
      return result.ok ? result.value : ([] as readonly RequestApproval[]);
    },
  });

  const trail = signatureTrail(request.requiredSignatures, approvals.data ?? []);
  const signed = trail.filter((segment) => segment.signed).length;

  return (
    <li
      className={styles.card}
      style={cssVars({ '--date-bg': colors.background, '--date-fg': colors.foreground, '--date-accent': statusAccentColor(request.status) })}
    >
      <div className={styles.dateBox}>
        <span className={styles.dateMonth}>{MONTHS_SHORT[request.range.start.month - 1]}</span>
        <span className={styles.dateDay}>{request.range.start.day}</span>
      </div>

      <div className={styles.body}>
        <div className={styles.range}>{formatDateRange(request.range)}</div>
        <div className={styles.meta}>
          {request.folio} · {request.businessDays} días hábiles
          {request.replacementName.length > 0 && ` · reemplazo ${request.replacementName}`}
          {request.replacementDecision === 'pending' && ' (sin confirmar)'}
          {request.replacementDecision === 'accepted' && ' (acepta cubrirte)'}
        </div>

        {request.replacementDecision === 'declined' && (
          <div className={styles.reason}>
            <span className={styles.reasonKey}>{request.replacementName} no puede cubrirte</span>
            <span className={styles.reasonValue}>{request.replacementReason}</span>
          </div>
        )}

        {/* Who has to sign, and how far that has got. Named rather than counted:
            "sin firmas todavía" is only actionable if you know whose. */}
        <div className={styles.route}>
          <span className={styles.routeText}>
            {trail
              .map((segment, index) =>
                index === 0
                  ? `Aprueba ${segment.approverName ?? 'tu líder'}`
                  : `visto bueno de ${segment.approverName ?? 'un segundo líder'}`,
              )
              .join(' · ')}
          </span>
          <Chip
            background={signed === 0 ? 'var(--color-surface-subtle-alt)' : 'var(--color-success-bg)'}
            foreground={
              signed === 0 ? 'var(--color-text-tertiary-alt)' : 'var(--color-success-ink)'
            }
          >
            {/*
              «Doble» sólo cuando de verdad se pidieron dos. Con una sola firma
              requerida esto decía «Doble visto bueno completo» sobre una única
              rúbrica, y ahora que el área puede registrar vacaciones —que nacen
              con una firma— es lo que se lee casi siempre.
            */}
            {signed === 0
              ? 'Sin firmas todavía'
              : signed === request.requiredSignatures
                ? request.requiredSignatures === 2
                  ? 'Doble visto bueno completo'
                  : 'Firmada'
                : `${String(signed)} de ${String(request.requiredSignatures)} firmas`}
          </Chip>
        </div>

        {request.resolutionReason !== null && (
          <div className={styles.reason}>
            <span className={styles.reasonKey}>
              {request.status === 'Cancelada' ? 'Motivo de la cancelación' : 'Motivo del rechazo'}
            </span>
            <span className={styles.reasonValue}>{request.resolutionReason}</span>
          </div>
        )}

        {/*
          * Where this one came from.
          *
          * The link was being written to Firestore and read back by the codec and
          * then shown to nobody, so a rescheduled request appeared in the list as
          * an unrelated one — the reader had two cards with overlapping stories
          * and nothing saying they were the same story. The rejected original
          * stays in the list on purpose: it is what a rejection *is*, evidence,
          * and the audit log and the emails already quote its folio.
          */}
        {/*
          * The attempts this one replaced, folded away.
          *
          * Collapsed rather than listed: the reader came to see where their
          * holiday stands, and three refusals unrolled above the answer bury it.
          * Open, it keeps what only those records hold — the dates that were
          * asked for and the reason each was refused — which is the part that
          * would have been lost by merging them into one row.
          */}
        {history.length > 0 && (
          <div className={styles.historyBlock}>
            <button
              type="button"
              className={styles.historyToggle}
              onClick={() => {
                setShowHistory((current) => !current);
              }}
              aria-expanded={showHistory}
            >
              <span aria-hidden="true">↺</span>{' '}
              {history.length === 1
                ? 'Reagendada una vez'
                : `Reagendada ${String(history.length)} veces`}
              <span className={styles.historyChevron} aria-hidden="true">
                {showHistory ? '▾' : '▸'}
              </span>
            </button>

            {showHistory && (
              <ol className={styles.historyList}>
                {history.map((previous) => (
                  <li key={previous.id} className={styles.historyItem}>
                    <span className={styles.historyDates}>{formatDateRange(previous.range)}</span>
                    <span className={styles.historyMeta}>
                      {previous.folio} · {previous.status}
                    </span>
                    {previous.resolutionReason !== null && (
                      <span className={styles.historyReason}>{previous.resolutionReason}</span>
                    )}
                  </li>
                ))}
              </ol>
            )}
          </div>
        )}

        {request.notes.length > 0 && <div className={styles.detail}>{request.notes}</div>}

        <div className={styles.tags}>
          {/*
            Aquí iba «Falta el registro firmado», en rojo, sobre toda solicitud
            sin PDF. El portal es ahora ese registro y ya no se pide ningún papel,
            así que el renglón sólo queda cuando dice algo: que la solicitud trae
            un registro de antes, o que la registró el área en nombre de alguien.
          */}
          {(request.pdfPath !== null || request.assignedByName !== null) && (
            <span className={styles.pdfLink}>
              {request.pdfPath !== null
                ? '▤ Registro adjunto'
                : `▤ Registrada por ${request.assignedByName ?? ''}`}
            </span>
          )}
          {request.replacementName.length > 0 && (
            <Chip background="var(--color-brand-bg)" foreground="var(--color-brand-ink)">
              ↺ {request.replacementName}
            </Chip>
          )}
        </div>
      </div>

      {/*
        La vida de la solicitud, como escalera vertical.

        Cinco pasos, o cuatro: el del segundo líder sólo existe si esta solicitud
        pide dos firmas. Ver `lifecycleSteps`.
      */}
      <ol className={styles.trailCell}>
        {lifecycleSteps(request.status, request.requiredSignatures).map((step) => (
          <li
            key={step.label}
            className={cx(
              styles.trailStep,
              step.state === 'done' && styles.trailDone,
              step.state === 'current' && styles.trailCurrent,
              step.state === 'rejected' && styles.trailRejected,
            )}
            aria-current={step.state === 'current' ? 'step' : undefined}
          >
            <span className={styles.trailDot} aria-hidden="true" />
            <span className={styles.trailLabel}>{step.label}</span>
          </li>
        ))}
      </ol>

      <div className={styles.statusCell}>
        <Chip background={colors.background} foreground={colors.foreground}>
          {request.status}
        </Chip>

        {isCancelable(request.status) && (
          <button type="button" className={styles.cancelButton} onClick={onCancel}>
            Cancelar
          </button>
        )}
        {/* Quien eligió el respaldo es quien mejor sabe quién más puede
            recoger su trabajo, así que la salida vive en su tarjeta. */}
        {request.replacementDecision === 'declined' && (
          <button type="button" className={styles.replacePick} onClick={onChangeReplacement}>
            Elegir otro respaldo
          </button>
        )}
        {isReschedulable(request.status) && (
          <button type="button" className={styles.rescheduleButton} onClick={onReschedule}>
            <span className={styles.rescheduleGlyph} aria-hidden="true">
              ↺
            </span>{' '}
            Reagendar
          </button>
        )}
      </div>
    </li>
  );
};

/**
 * Cancellation.
 *
 * The reason is mandatory and comes from the catalogue rather than a free-text box: a
 * cancelled request leaves a hole in the sub area's plan that somebody later has to
 * understand.
 */
const CancelDialog = ({
  request,
  onClose,
  onDone,
}: {
  request: VacationRequest;
  onClose: () => void;
  onDone: () => Promise<void>;
}): ReactNode => {
  const session = useSession();
  const toast = useToast();
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  const reasons = reasonsForScope(session.config.catalogs, 'cancelacion');

  const submit = async (): Promise<void> => {
    if (reason.length === 0) return;
    setBusy(true);

    const result = await getServices().requests.cancel({ requestId: request.id, reason });
    setBusy(false);

    if (!result.ok) {
      toast.bad(
        result.error.kind === 'unavailable'
          ? 'No se pudo cancelar. Revisa tu conexión.'
          : 'No tienes permiso para cancelar esta solicitud.',
      );
      return;
    }

    toast.ok(`${request.folio} quedó cancelada.`);
    await onDone();
  };

  return (
    <Modal
      title={`Cancelar ${request.folio}`}
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Conservar
          </Button>
          <Button
            variant="danger"
            busy={busy}
            disabled={reason.length === 0}
            onClick={() => {
              void submit();
            }}
          >
            Cancelar la solicitud
          </Button>
        </>
      }
    >
      <div className={shared.stack}>
        <p className={shared.rowNote}>
          Vas a cancelar tus días del {formatDateRange(request.range)}. Tu líder verá el motivo y
          la sub área recupera el espacio en el calendario.
        </p>

        <Field label="Motivo de la cancelación">
          {(id) => (
            <ReasonPicker id={id} reasons={reasons} value={reason} onChange={setReason} />
          )}
        </Field>
      </div>
    </Modal>
  );
};

/**
 * Elegir otro respaldo sin rehacer la solicitud.
 *
 * Cuando el primero dice que no puede, el folio no tiene nada de malo: las
 * fechas, las notas de entrega y la fila de firmas siguen siendo buenas. Lo
 * único que cambió es quién cubre, y obligar a capturarlo todo otra vez sería
 * castigar a la persona por la agenda de alguien más.
 *
 * Elige el solicitante, no el líder: sabe qué trabajo deja pendiente y quién
 * puede recogerlo. El nuevo entra sin confirmar —tampoco se le ha preguntado— y
 * el candado del líder vuelve a cerrarse hasta que conteste.
 */
const PickReplacementDialog = ({
  request,
  onClose,
  onDone,
}: {
  request: VacationRequest;
  onClose: () => void;
  onDone: () => Promise<void>;
}): ReactNode => {
  const session = useSession();
  const toast = useToast();
  const [search, setSearch] = useState('');
  const [picked, setPicked] = useState<PersonId | null>(null);
  const [busy, setBusy] = useState(false);

  const colleagues = useQuery({
    queryKey: ['requests', 'colleagues', session.areaId, session.personId],
    queryFn: async () => {
      const result = await getServices().people.list(
        { areaId: session.areaId, status: 'active' },
        { limit: 200 },
      );
      return result.ok
        ? result.value.items.filter(
            // Ni uno mismo, ni quien acaba de decir que no puede.
            (p) => p.id !== session.personId && p.id !== request.replacementPersonId,
          )
        : [];
    },
  });

  /* Quién del área estará fuera esos días: no puede cubrirlos. La misma regla
     que el asistente y que el servidor; ver `awayDuring`. */
  const areaAbsences = useQuery({
    queryKey: ['requests', 'areaAbsences', request.areaId, request.id],
    queryFn: async () => {
      const result = await getServices().requests.list(
        { areaId: request.areaId, statuses: OCCUPYING_STATUSES, touching: request.range },
        { limit: 200 },
      );
      return result.ok ? result.value.items : [];
    },
  });
  const away = awayDuring(areaAbsences.data ?? [], request.range);

  const needle = foldForSearch(search);
  const shown = (colleagues.data ?? [])
    .filter((p) => (needle.length === 0 ? true : foldForSearch(p.displayName).includes(needle)))
    // Quien estará fuera, al final y sin poder elegirse.
    .sort((a, b) => Number(away.has(a.id)) - Number(away.has(b.id)));
  const chosen = picked !== null && !away.has(picked) ? picked : null;

  const submit = async (): Promise<void> => {
    if (chosen === null) return;
    setBusy(true);

    const result = await getServices().requests.changeReplacement(request.id, chosen);
    setBusy(false);

    if (!result.ok) {
      toast.bad(
        serverAnswer(result.error, 'changeReplacement') ??
          (result.error.kind === 'unavailable'
            ? 'No se pudo cambiar el respaldo. Revisa tu conexión.'
            : 'No se pudo cambiar el respaldo.'),
      );
      return;
    }

    toast.ok('Le avisamos a tu nuevo respaldo.');
    await onDone();
  };

  return (
    <Modal
      title={`Nuevo respaldo para ${request.folio}`}
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            busy={busy}
            disabled={chosen === null}
            onClick={() => {
              void submit();
            }}
          >
            Designar respaldo
          </Button>
        </>
      }
    >
      <div className={shared.stack}>
        <p className={shared.rowNote}>
          Tus días del {formatDateRange(request.range)} siguen en pie y conservas el folio. La
          persona que elijas recibirá el aviso para confirmar.
        </p>

        <input
          type="search"
          className={styles.replaceSearch}
          placeholder="Buscar por nombre…"
          aria-label="Buscar a quién designar"
          value={search}
          onChange={(event) => {
            setSearch(event.target.value);
          }}
        />

        <ul className={styles.replaceList}>
          {shown.map((person) => {
            const awayNote = away.get(person.id);
            return (
              <li key={person.id}>
                <button
                  type="button"
                  className={cx(
                    styles.replaceOption,
                    chosen === person.id && styles.replaceOptionPicked,
                  )}
                  aria-pressed={chosen === person.id}
                  disabled={awayNote !== undefined}
                  onClick={() => {
                    setPicked(person.id);
                  }}
                >
                  <Avatar name={person.displayName} color={seedColorForSubArea('')} size={30} />
                  <span className={styles.replaceName}>
                    {person.displayName}
                    {awayNote !== undefined && (
                      <span className={styles.replaceAway}> · {awayNote}</span>
                    )}
                  </span>
                </button>
              </li>
            );
          })}
          {shown.length === 0 && (
            <li className={styles.replaceEmpty}>Nadie con ese nombre en tu área.</li>
          )}
        </ul>
      </div>
    </Modal>
  );
};
